import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { ref, onValue, set, get } from 'firebase/database';
import { LogIn } from 'lucide-react';

// ─── Canvas ───────────────────────────────────────────────────────────────────
export const CANVAS_W = 800;
export const CANVAS_H = 300;
const GROUND_Y       = 236;   // canvas px
const HIGH_SCORE_KEY = 'pokeDashHighScore';
export const PLAYER_SPRITE = '/sprite.png'; // Togepi
export const BIRD_SPRITE = 'https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/16.png'; // Pidgey

// ─── Game rules ───────────────────────────────────────────────────────────────
// The mechanics and numbers of Chrome's offline dino game (Chromium source, BSD licence).
// Everything here is in that game's own units: a 600-unit-wide world, speeds in units per frame at 60fps.
const WORLD_W        = 600;
const SCALE          = CANVAS_W / WORLD_W;
export const MS_PER_FRAME = 1000 / 60;
const START_SPEED    = 6;
const MAX_SPEED      = 13;
const ACCELERATION   = 0.001;   // speed gained per frame (about two minutes to reach top speed)
const GAP_COEFFICIENT     = 0.6;
const MAX_GAP_COEFFICIENT = 1.5;
const MAX_OBSTACLE_LENGTH = 3;  // balls can come in groups of up to 3
const MAX_OBSTACLE_DUPLICATION = 2;
const CLEAR_TIME          = 3000; // ms of open ground before the first obstacle
const GAMEOVER_CLEAR_TIME = 750;  // ms before a tap can restart, so you don't restart by accident
const SCORE_COEFFICIENT   = 0.025;
const GRAVITY             = 0.6;
const INITIAL_JUMP_VELOCITY = -10;
const DROP_VELOCITY       = -5;
const SPEED_DROP_COEFFICIENT = 3;
const MIN_JUMP_HEIGHT     = 30;   // rise where an early release turns into a short hop
const MAX_JUMP_HEIGHT     = 63;   // rise where a held jump levels off

export const PLAYER_X = 48;
export const PLAYER = { w: 46, h: 46, duckW: 54, duckH: 28 };
// Small and big Poké Balls stand in for the cacti, Pidgey for the pterodactyl. Once it's faster, a Pokémon now and
// then comes rolling at you instead (weight: how often each type is picked, against 1 for the others)
const OBSTACLE_TYPES = [
  { type: 'ball',    w: 26, h: 26, minGap: 120, multipleSpeed: 4,   minSpeed: 0 },
  { type: 'bigBall', w: 34, h: 34, minGap: 120, multipleSpeed: 7,   minSpeed: 0 },
  { type: 'bird',    w: 46, h: 40, minGap: 150, multipleSpeed: 999, minSpeed: 8.5, speedOffset: 0.8 },
  { type: 'roller',  w: 34, h: 34, minGap: 150, multipleSpeed: 999, minSpeed: 9.5, weight: 0.35 },
  { type: 'diglett', w: 30, h: 32, minGap: 150, multipleSpeed: 999, minSpeed: 7,   weight: 0.3 },
];

// Diglett travels along inside the earth, where you can see it coming, and pops up as it nears Togepi: jump it. It can
// only hit you once it's up. It starts popping the same time before reaching you at any speed, so there's always time to
// react (tested: jumping a fifth of a second after it starts coming up clears it, even at top speed)
const DIGLETT_ID = 50;
const POP_LEAD = 24;     // it starts coming up this many 60fps frames before it would reach Togepi
const POP_FRAMES = 6;    // how long coming up takes

// How a bird flies. Ducking always gets under the 'low' and 'bob' ones (Togepi ducked is 22 high, and they never come
// lower than 24), so every bird can be beaten:
//   ground: skims the ground, so jump it
//   low:    too low to run under, so duck (or jump it early)
//   bob:    bounces up and down as it flies, then as it gets close it swoops to Togepi's height and follows it, like a
//           hawk: jump and it follows you up, stand and it dives at you. Only ducking gets under it. (Tested against
//           every possible jump timing: none gets over it, and ducking is always safe)
const FLIGHTS = [
  { kind: 'ground', rise: 0 },
  { kind: 'low',    rise: 18 },
  { kind: 'bob',    rise: 41, bob: 15 },
];
const BOB_RATE = 0.16;     // radians per 60fps frame: one bounce about every two thirds of a second
const SWOOP_RANGE = 200;   // how far off it starts to swoop: at top speed, a quarter of a second's warning
const SWOOP_SPEED = 3.5;   // how fast it can climb or dive, per frame
const SWOOP_LOW = 26;      // its lowest: always clear of a ducking Togepi (22 high)
const SWOOP_TOP = 100;

// The rolling Pokémon (PokeAPI numbers), each its own size. And the legendary birds that sometimes fly in place of a
// Pidgey: just for looks, with exactly a Pidgey's size and flight
export const ROLLERS = [{ id: 143, w: 44 }];  // Snorlax
export const LEGENDS = [144, 145, 146];  // Articuno, Zapdos, Moltres
const LEGEND_CHANCE = 0.12;
export const POKEMON_IDS = [...ROLLERS.map(r => r.id), ...LEGENDS, DIGLETT_ID];
// Sprites sit next to Pidgey's, wherever that is (on the site, GitHub; in Discord, through its proxy)
export const pokemonSprite = (birdSprite, id) => `${birdSprite.slice(0, birdSprite.lastIndexOf('/') + 1)}${id}.png`;

// Birds that have to be ducked under: the self-playing preview asks this
export const mustDuck = (o) => o.t.type === 'bird' && o.flight.kind !== 'ground';

const randInt = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;
const pick = (list) => list[randInt(0, list.length - 1)];

function pickType(options) {
  let roll = Math.random() * options.reduce((sum, t) => sum + (t.weight ?? 1), 0);
  return options.find(t => (roll -= t.weight ?? 1) < 0) || options[options.length - 1];
}

export function freshState() {
  return {
    y: 0, vy: 0,                       // Togepi's rise above the ground, and its velocity (negative is up)
    jumping: false, reachedMin: false, released: false, speedDrop: false,
    ducking: false, duckHeld: false,
    obstacles: [], history: [],
    inputs: [],                        // recent presses, [when (runTime ms), 'J'|'R'|'D'|'U'], so spectators replay them
    noSpawn: false,                    // a spectator's copy: obstacles only ever come from the player
    speed: START_SPEED, distance: 0, runTime: 0, groundOffset: 0,
    lastTime: null, lastScore: 0, overAt: 0,
  };
}

// A press or release: J jump, R let go of jump, D duck, U let go of duck. The game and spectators' replays both use
// this, so a replay does exactly what the player did
export function applyInput(s, input) {
  if (input === 'J') startJump(s);
  else if (input === 'R') { s.released = true; endJump(s); }
  else if (input === 'D') {
    s.duckHeld = true;
    if (s.jumping) { s.speedDrop = true; s.vy = 1; }  // in the air: drop down fast
    else s.ducking = true;
  } else if (input === 'U') { s.duckHeld = false; s.ducking = false; s.speedDrop = false; }
}

const INPUTS_KEPT_MS = 2000;
function recordInput(s, input) {
  s.inputs.push([Math.round(s.runTime * 10) / 10, input]);
  while (s.inputs.length && s.inputs[0][0] < s.runTime - INPUTS_KEPT_MS) s.inputs.shift();
}

export function startJump(s) {
  if (s.jumping || s.ducking) return;
  s.jumping = true;
  s.vy = INITIAL_JUMP_VELOCITY - s.speed / 10;
  s.reachedMin = false;
  s.released = false;
  s.speedDrop = false;
}

export function endJump(s) {
  if (s.reachedMin && s.vy < DROP_VELOCITY) s.vy = DROP_VELOCITY;
}

function addObstacle(s) {
  // Any type that's fast enough to appear, and not the same type three times running
  const options = OBSTACLE_TYPES.filter(t => s.speed >= t.minSpeed
    && !(s.history.length >= MAX_OBSTACLE_DUPLICATION && s.history.every(h => h === t.type)));
  const t = pickType(options);
  let size = randInt(1, MAX_OBSTACLE_LENGTH);
  if (size > 1 && t.multipleSpeed > s.speed) size = 1;
  const roller = t.type === 'roller' ? pick(ROLLERS) : null;
  const unit = roller ? roller.w : t.w;  // the width of one of them
  const w = unit * size;
  // The gap is a distance: always enough to land and jump again at the current speed, plus some random extra
  const minGap = Math.round(w * s.speed + t.minGap * GAP_COEFFICIENT);
  const gap = randInt(minGap, Math.round(minGap * MAX_GAP_COEFFICIENT));
  const flight = t.type === 'bird' ? pick(FLIGHTS) : null;
  s.obstacles.push({
    t, size, unit, w, gap, x: WORLD_W, rot: 0, spawnedNext: false, age: 0,
    flight, rise: flight ? flight.rise : 0, bobPhase: Math.random() * Math.PI * 2,
    pokemon: roller ? roller.id : t.type === 'diglett' ? DIGLETT_ID
      : t.type === 'bird' && Math.random() < LEGEND_CHANCE ? pick(LEGENDS) : null,
    pop: 0,  // Diglett: 0 under the ground, 1 fully up
    speedOffset: t.speedOffset ? (Math.random() > 0.5 ? t.speedOffset : -t.speedOffset) : 0,
  });
  s.history.unshift(t.type);
  s.history.length = Math.min(s.history.length, MAX_OBSTACLE_DUPLICATION);
}

// Hitboxes in world units: x from the left, y as rise above the ground
function playerBox(s) {
  return s.ducking
    ? { x: PLAYER_X + 6, y: s.y, w: PLAYER.duckW - 12, h: PLAYER.duckH - 6 }
    : { x: PLAYER_X + 9, y: s.y + 2, w: PLAYER.w - 18, h: PLAYER.h - 6 };
}

export function hits(s) {
  const p = playerBox(s);
  return s.obstacles.some((o) => {
    if (o.t.type === 'bird') {
      const b = { x: o.x + 4, y: o.rise + 6, w: o.w - 8, h: o.t.h - 14 };
      return p.x < b.x + b.w && p.x + p.w > b.x && p.y < b.y + b.h && p.y + p.h > b.y;
    }
    if (o.t.type === 'diglett') {  // only once it's coming up, and only as tall as it's come
      const b = { x: o.x + 5, y: 0, w: o.w - 10, h: (o.t.h - 4) * o.pop };
      return o.pop > 0.3 && p.x < b.x + b.w && p.x + p.w > b.x && p.y < b.y + b.h;
    }
    // Balls (and the rolling Pokémon) are round, so test each one as a circle
    for (let k = 0; k < o.size; k++) {
      const r = o.unit / 2;
      const cx = o.x + k * o.unit + r, cy = r;
      const nx = Math.max(p.x, Math.min(cx, p.x + p.w));
      const ny = Math.max(p.y, Math.min(cy, p.y + p.h));
      if ((cx - nx) ** 2 + (cy - ny) ** 2 < (r * 0.88) ** 2) return true;
    }
    return false;
  });
}

// One step of the game, f = how many 60fps frames have passed
export function step(s, dt) {
  const f = dt / MS_PER_FRAME;
  s.runTime += dt;
  s.speed = Math.min(MAX_SPEED, s.speed + ACCELERATION * f);
  s.distance += s.speed * f;
  s.groundOffset = (s.groundOffset + s.speed * f) % WORLD_W;

  if (s.jumping) {
    s.y -= (s.speedDrop ? s.vy * SPEED_DROP_COEFFICIENT : s.vy) * f;
    s.vy += GRAVITY * f;
    if (s.y > MIN_JUMP_HEIGHT || s.speedDrop) {
      s.reachedMin = true;
      if (s.released) endJump(s);
    }
    if (s.y > MAX_JUMP_HEIGHT || s.speedDrop) endJump(s);
    if (s.y <= 0) {
      s.y = 0; s.vy = 0;
      s.jumping = false; s.speedDrop = false;
      if (s.duckHeld) s.ducking = true;
    }
  }

  s.obstacles.forEach((o) => {
    const dx = (s.speed + o.speedOffset) * f;
    o.x -= dx;
    o.rot -= dx / (o.unit / 2);
    o.age += f;
    if (o.t.type === 'diglett' && o.x - (PLAYER_X + PLAYER.w) < s.speed * POP_LEAD) o.pop = Math.min(1, o.pop + f / POP_FRAMES);
    if (o.flight?.bob) {
      if (o.x - (PLAYER_X + PLAYER.w) > SWOOP_RANGE) {
        o.rise = o.flight.rise + Math.sin(o.age * BOB_RATE + o.bobPhase) * o.flight.bob;
      } else {  // swooping: heads for Togepi's height
        const target = Math.min(SWOOP_TOP, Math.max(SWOOP_LOW, s.y + 8));
        o.rise += Math.max(-SWOOP_SPEED * f, Math.min(SWOOP_SPEED * f, target - o.rise));
      }
    }
  });
  s.obstacles = s.obstacles.filter(o => o.x + o.w > 0);

  if (!s.noSpawn && s.runTime > CLEAR_TIME) {
    const last = s.obstacles[s.obstacles.length - 1];
    if (!last) addObstacle(s);
    else if (!last.spawnedNext && last.x + last.w + last.gap < WORLD_W) {
      last.spawnedNext = true;
      addObstacle(s);
    }
  }
}

function drawBall(ctx, cx, cy, r, rot, big) {
  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(rot);
  ctx.beginPath();
  ctx.arc(0, 0, r, Math.PI, 0);
  ctx.fillStyle = big ? '#2563eb' : '#ee1515'; // big ones are Great Balls
  ctx.fill();
  ctx.beginPath();
  ctx.arc(0, 0, r, 0, Math.PI);
  ctx.fillStyle = '#ffffff';
  ctx.fill();
  if (big) {
    ctx.fillStyle = '#ef4444';
    ctx.fillRect(-r * 0.62, -r * 0.82, r * 0.34, r * 0.5);
    ctx.fillRect(r * 0.28, -r * 0.82, r * 0.34, r * 0.5);
  }
  ctx.beginPath();
  ctx.arc(0, 0, r, 0, Math.PI * 2);
  ctx.lineWidth = Math.max(1.5, r * 0.14);
  ctx.strokeStyle = '#18181b';
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(-r, 0);
  ctx.lineTo(r, 0);
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(0, 0, r * 0.32, 0, Math.PI * 2);
  ctx.fillStyle = '#ffffff';
  ctx.fill();
  ctx.lineWidth = Math.max(1.2, r * 0.1);
  ctx.stroke();
  ctx.restore();
}

// ─── Togepi's running animation ─────────────────────────────────────────────
// Only how Togepi is drawn: the hitbox (playerBox) and every rule above stay exactly as they were, so scores are fair.
// The sprite is one picture, so its feet and its small right arm are cut out of it (these shapes are in the 500 x 499
// picture's own pixels) and moved on their own. The big front arm is painted over the body, so it stays put.
const SPRITE_W = 500, SPRITE_H = 499;
const LEFT_FOOT  = [[95, 400], [128, 410], [150, 425], [172, 440], [185, 448], [185, 499], [95, 499]];
const RIGHT_FOOT = [[395, 398], [470, 398], [470, 499], [325, 499], [325, 458], [345, 445], [362, 430], [375, 418]];
const RIGHT_ARM  = [[446, 222], [500, 222], [500, 302], [446, 302]];
const ARM_PIVOT  = [446, 262];  // where the arm meets the body
const STRIDE = 0.06;            // steps per unit of distance, so the legs speed up with the game
const togepiParts = new WeakMap();

function partsOf(img) {
  let parts = togepiParts.get(img);
  if (parts) return parts;
  const kx = (img.naturalWidth || img.width) / SPRITE_W, ky = (img.naturalHeight || img.height) / SPRITE_H;
  const cut = (shapes, without) => {
    const c = document.createElement('canvas');
    c.width = img.naturalWidth || img.width;
    c.height = img.naturalHeight || img.height;
    const g = c.getContext('2d');
    g.beginPath();
    if (without) g.rect(0, 0, c.width, c.height);  // everything except the shapes
    shapes.forEach((shape) => {
      shape.forEach(([x, y], i) => (i ? g.lineTo(x * kx, y * ky) : g.moveTo(x * kx, y * ky)));
      g.closePath();
    });
    g.clip('evenodd');
    g.drawImage(img, 0, 0);
    return c;
  };
  parts = { body: cut([LEFT_FOOT, RIGHT_FOOT, RIGHT_ARM], true), leftFoot: cut([LEFT_FOOT]), rightFoot: cut([RIGHT_FOOT]), arm: cut([RIGHT_ARM]) };
  togepiParts.set(img, parts);
  return parts;
}

// Where the feet and arm are, and the body's lean, at this point in the run
// Jumping: stretched tall with feet kicked down and apart and the arm thrown up on the way up, easing into a
// ready-to-land pose on the way down
const JUMP_UP   = { left: [-18, 14], right: [20, 12], arm: -0.75, tilt: -0.09, stretch: 1.1 };
const JUMP_DOWN = { left: [-4, 2], right: [14, -6], arm: -0.25, tilt: 0.05, stretch: 0.98 };

function togepiPose(s, running) {
  if (!running) return { left: [0, 0], right: [0, 0], arm: 0, tilt: 0, stretch: 1 };
  if (s.jumping) {
    const up = Math.min(1, Math.max(0, -s.vy / 8));  // 1 leaving the ground, 0 from the top of the jump onwards
    const mix = (a, b) => b + (a - b) * up;
    return {
      left: [mix(JUMP_UP.left[0], JUMP_DOWN.left[0]), mix(JUMP_UP.left[1], JUMP_DOWN.left[1])],
      right: [mix(JUMP_UP.right[0], JUMP_DOWN.right[0]), mix(JUMP_UP.right[1], JUMP_DOWN.right[1])],
      arm: mix(JUMP_UP.arm, JUMP_DOWN.arm), tilt: mix(JUMP_UP.tilt, JUMP_DOWN.tilt), stretch: mix(JUMP_UP.stretch, JUMP_DOWN.stretch),
    };
  }
  const phase = s.distance * STRIDE;
  const swing = Math.cos(phase) * 22, lift = Math.sin(phase) * 30;
  return {
    left: [swing, -Math.max(0, lift)],    // each foot swings forward and back, lifting on its way forward
    right: [-swing, -Math.max(0, -lift)],
    arm: Math.sin(phase) * 0.35,
    tilt: s.ducking ? 0 : Math.sin(phase) * 0.05,
    stretch: 1,
  };
}

function drawTogepi(ctx, img, x, y, w, h, pose) {
  const parts = partsOf(img);
  ctx.save();
  ctx.translate(x + w / 2, y + h);  // lean from the bottom middle, where Togepi stands
  ctx.rotate(pose.tilt);
  ctx.scale(1 / Math.sqrt(pose.stretch), pose.stretch);  // taller and thinner, or squatter, keeping its size
  ctx.translate(-w / 2, -h);
  ctx.scale(w / SPRITE_W, h / SPRITE_H);  // from here on, in the picture's own pixels
  // Feet first, so a lifted foot tucks behind the body
  ctx.drawImage(parts.leftFoot, pose.left[0], pose.left[1], SPRITE_W, SPRITE_H);
  ctx.drawImage(parts.rightFoot, pose.right[0], pose.right[1], SPRITE_W, SPRITE_H);
  ctx.drawImage(parts.body, 0, 0, SPRITE_W, SPRITE_H);
  ctx.translate(ARM_PIVOT[0], ARM_PIVOT[1]);
  ctx.rotate(pose.arm);
  ctx.translate(-ARM_PIVOT[0], -ARM_PIVOT[1]);
  ctx.drawImage(parts.arm, 0, 0, SPRITE_W, SPRITE_H);
  ctx.restore();
}

// Ducking: Togepi slides along the ground feet first, tipped back like a baseball slide, and holds still. It's drawn
// to fit the ducking hitbox's height, so a bird you duck under clearly passes over it
const SLIDE_TILT = -1.25;      // radians: about 70 degrees back
const SLIDE_SIZE = 38;         // world units: the picture's size before tipping
const SLIDE_FLATTEN = 0.8;     // a little flatter, to stay inside the duck height

// Dust puffs and bits of grass kicked up behind a slide. Worked out from how far the run has gone rather than kept
// anywhere, so they cost nothing and can't touch the game: each one loops through its short life again and again
const DUST = 7, GRASS = 6;
function drawSlideSpray(ctx, cx, groundY, moved) {
  const back = cx - 22;  // Togepi's back, where its head's tipped to
  ctx.save();
  for (let i = 0; i < DUST; i++) {
    const age = (moved * 0.012 + i / DUST) % 1;  // 0 just kicked up, 1 gone
    ctx.globalAlpha = (1 - age) * 0.8;
    ctx.fillStyle = i % 2 ? '#f0dcc0' : '#dcbf96';
    ctx.beginPath();
    ctx.arc(back - age * 70 - (i % 3) * 4, groundY - 4 - age * 16 - (i % 2) * 4, 3 + age * 8, 0, Math.PI * 2);
    ctx.fill();
  }
  for (let i = 0; i < GRASS; i++) {
    const age = (moved * 0.016 + i / GRASS + 0.07) % 1;
    ctx.globalAlpha = 1 - age * age;
    ctx.fillStyle = i % 2 ? '#7ed957' : '#4fae3f';
    ctx.save();
    ctx.translate(back + 6 - age * 55 - (i % 3) * 5, groundY - 2 - Math.sin(age * Math.PI) * (16 + (i % 3) * 7));  // up and over
    ctx.rotate(age * 7 + i);
    ctx.fillRect(-1, -4, 2, 8);
    ctx.restore();
  }
  ctx.restore();
}

function drawSlide(ctx, img, cx, groundY) {
  const size = SLIDE_SIZE * SCALE;
  const tall = size * 0.9 * SLIDE_FLATTEN;  // how tall it ends up (the egg nearly fills the picture)
  ctx.save();
  ctx.translate(cx + 4, groundY - tall / 2);
  ctx.scale(1, SLIDE_FLATTEN);
  ctx.rotate(SLIDE_TILT);
  ctx.drawImage(img, -size / 2, -size / 2, size, size);
  ctx.restore();
}

// ─── The scenery ────────────────────────────────────────────────────────────
// A sunny day: sky, sun, clouds and two layers of hills. Further-off things scroll slower (`moved` is how far the run has
// gone, in canvas px), which gives it depth. Only scenery: nothing here touches the game.
function drawHills(ctx, offset, base, height, k, colour) {
  ctx.fillStyle = colour;
  ctx.beginPath();
  ctx.moveTo(0, GROUND_Y);
  for (let x = 0; x <= CANVAS_W; x += 10) {
    const p = x + offset;
    ctx.lineTo(x, base - height * (0.6 * Math.sin(p * k) + 0.4 * Math.sin(p * k * 2.3 + 1.7) + 1) / 2);
  }
  ctx.lineTo(CANVAS_W, GROUND_Y);
  ctx.closePath();
  ctx.fill();
}

function drawCloud(ctx, x, y, size) {
  ctx.beginPath();
  ctx.arc(x, y, size * 0.5, 0, Math.PI * 2);
  ctx.arc(x + size * 0.45, y - size * 0.2, size * 0.6, 0, Math.PI * 2);
  ctx.arc(x + size * 0.95, y, size * 0.45, 0, Math.PI * 2);
  ctx.rect(x, y - size * 0.1, size * 0.95, size * 0.5);
  ctx.fill();
}

function drawBackdrop(ctx, moved) {
  const sky = ctx.createLinearGradient(0, 0, 0, GROUND_Y);
  sky.addColorStop(0, '#5ab8f0');
  sky.addColorStop(1, '#d6f0ff');
  ctx.fillStyle = sky;
  ctx.fillRect(0, 0, CANVAS_W, GROUND_Y);

  // The sun, with a soft glow
  const glow = ctx.createRadialGradient(690, 62, 10, 690, 62, 90);
  glow.addColorStop(0, 'rgba(255,240,170,0.9)');
  glow.addColorStop(1, 'rgba(255,240,170,0)');
  ctx.fillStyle = glow;
  ctx.fillRect(590, 0, 200, 160);
  ctx.fillStyle = '#ffe47a';
  ctx.beginPath();
  ctx.arc(690, 62, 26, 0, Math.PI * 2);
  ctx.fill();

  // Clouds drift by slowly
  ctx.fillStyle = 'rgba(255,255,255,0.92)';
  const span = CANVAS_W + 240;
  [[0, 50, 46], [260, 92, 34], [520, 40, 40], [760, 110, 28]].forEach(([start, y, size]) => {
    drawCloud(ctx, ((start - moved * 0.08) % span + span) % span - 120, y, size);
  });

  drawHills(ctx, moved * 0.2, GROUND_Y - 30, 70, 0.006, '#9fd88a');   // far hills
  drawHills(ctx, moved * 0.45, GROUND_Y - 4, 46, 0.011, '#6cc35a');   // near hills
}

// Draws one frame of the game. Shared by the game itself and the Arcade card's self-playing preview.
export function drawScene(ctx, s, sprites, now, running) {
  ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);
  drawBackdrop(ctx, s.distance * SCALE);

  // The ground: grass on top, earth below, with tufts and pebbles that scroll at running speed so you feel the speed
  ctx.fillStyle = '#b07a4a';
  ctx.fillRect(0, GROUND_Y, CANVAS_W, CANVAS_H - GROUND_Y);
  ctx.fillStyle = '#4fae3f';
  ctx.fillRect(0, GROUND_Y - 2, CANVAS_W, 12);
  ctx.fillStyle = '#7ed957';
  ctx.fillRect(0, GROUND_Y - 2, CANVAS_W, 3);
  ctx.fillStyle = '#3d8f31';
  ctx.fillRect(0, GROUND_Y + 9, CANVAS_W, 2);
  for (let i = 0; i < 26; i++) {
    const x = ((((i * 137) % WORLD_W) - s.groundOffset + WORLD_W) % WORLD_W) * SCALE;
    ctx.fillStyle = i % 2 ? '#8f5f36' : '#c99566';  // pebbles in the earth
    ctx.fillRect(x, GROUND_Y + 18 + (i % 3) * 13, (i % 2 ? 4 : 9) * SCALE, 3);
    if (i % 3 === 0) {  // a grass tuft
      ctx.fillStyle = '#7ed957';
      ctx.fillRect(x + 20, GROUND_Y - 6, 2, 5);
      ctx.fillRect(x + 24, GROUND_Y - 8, 2, 7);
      ctx.fillRect(x + 28, GROUND_Y - 5, 2, 4);
    }
  }

  // Togepi (squashed when ducking, bouncing with each step while running). Drawing only: see togepiPose
  const w = (s.ducking ? PLAYER.duckW : PLAYER.w) * SCALE;
  const h = (s.ducking ? PLAYER.duckH : PLAYER.h) * SCALE;
  const bob = running && !s.jumping && !s.ducking ? Math.abs(Math.sin(s.distance * STRIDE)) * 6 : 0;
  const px = PLAYER_X * SCALE;
  const py = GROUND_Y - s.y * SCALE - h - bob;
  if (sprites.player && s.ducking) {
    if (running) drawSlideSpray(ctx, px + w / 2, GROUND_Y - s.y * SCALE, s.distance * SCALE);
    drawSlide(ctx, sprites.player, px + w / 2, GROUND_Y - s.y * SCALE);
  } else if (sprites.player) {
    drawTogepi(ctx, sprites.player, px, py, w, h, togepiPose(s, running));
  } else {
    ctx.fillStyle = '#FCD34D';
    ctx.beginPath();
    ctx.ellipse(px + w / 2, py + h / 2, w / 2, h / 2, 0, 0, Math.PI * 2);
    ctx.fill();
  }

  // Obstacles
  s.obstacles.forEach((o) => {
    const pokemon = o.pokemon && sprites.pokemon?.[o.pokemon];
    if (o.t.type === 'diglett') {
      const cx = (o.x + o.w / 2) * SCALE;
      const tall = o.t.h * SCALE;
      if (pokemon) {
        // Its rocks come along the ground on their own; as it nears Togepi, its body rises up out of them. In the 96px
        // picture the body runs from row 37 to 50 and the rocks from 50 to 62. The rocks sit a little down in the grass:
        // row 59 is level with the ground
        const k = tall / 25, src = pokemon.width / 96;  // canvas px per picture row (body and rocks are 25 rows tall)
        const size = 96 * k, left = cx - size / 2;
        const rowY = (row) => GROUND_Y + 2 - (59 - row) * k;
        const sunk = (53 - 37) * k * (1 - o.pop);  // how far down into the rocks the body still is
        ctx.save();
        ctx.beginPath();
        ctx.rect(left, 0, size, rowY(53));  // hidden once it's sunk into the rocks
        ctx.clip();
        ctx.drawImage(pokemon, 0, 0, 96 * src, 53 * src, left, rowY(0) + sunk, size, 53 * k);
        ctx.restore();
        // The rocks from row 53, below the body, so before it pops up there's nothing but rocks
        ctx.drawImage(pokemon, 0, 53 * src, 96 * src, 43 * src, left, rowY(53), size, 43 * k);
      } else {
        ctx.fillStyle = '#a0522d';
        ctx.beginPath();
        ctx.ellipse(cx, GROUND_Y - (tall / 2) * o.pop, (o.w / 2) * SCALE, Math.max(4, (tall / 2) * o.pop), 0, 0, Math.PI * 2);
        ctx.fill();
      }
      return;
    }
    if (o.t.type === 'roller') {
      const r = (o.unit / 2) * SCALE;
      const cx = o.x * SCALE + r, cy = GROUND_Y - r;
      if (pokemon) {
        const size = (o.unit / 0.72) * SCALE;  // the sprite has empty space around the Pokémon
        ctx.save();
        ctx.translate(cx, cy);
        ctx.rotate(o.rot);
        ctx.drawImage(pokemon, -size / 2, -size / 2, size, size);
        ctx.restore();
      } else {
        drawBall(ctx, cx, cy, r, o.rot, false);
      }
      return;
    }
    if (o.t.type === 'bird') {
      const flap = Math.sin(now / 90) * 3 * SCALE;
      const cx = (o.x + o.w / 2) * SCALE;
      const cy = GROUND_Y - (o.rise + o.t.h / 2) * SCALE + flap;
      const sprite = pokemon || sprites.bird;
      if (sprite) {
        // The sprite has empty space around Pidgey. The legendaries fill far more of theirs, so they're drawn smaller,
        // to look the size they are to the game (a Pidgey's)
        const size = (pokemon ? 60 : 100) * SCALE;
        ctx.drawImage(sprite, cx - size / 2, cy - size / 2, size, size);
      } else {
        ctx.fillStyle = '#a16207';
        ctx.beginPath();
        ctx.ellipse(cx, cy, (o.w / 2) * SCALE, (o.t.h / 2.5) * SCALE, 0, 0, Math.PI * 2);
        ctx.fill();
      }
      return;
    }
    const r = (o.t.w / 2) * SCALE;
    for (let k = 0; k < o.size; k++) {
      drawBall(ctx, (o.x + k * o.t.w) * SCALE + r, GROUND_Y - r, r, o.rot, o.t.type === 'bigBall');
    }
  });
}

// ─── Spectating ─────────────────────────────────────────────────────────────
// A player's run, as a short text that's sent a few times a second for others to watch. Numbers only: anything that
// comes back in is checked and limited, since anyone could write one
export function snapshotOf(s, score, over) {
  const r = (n) => Math.round(n * 100) / 100;
  return JSON.stringify({
    v: 1, over: over ? 1 : 0, sc: score, rt: Math.round(s.runTime * 10) / 10, in: s.inputs,  // the run's clock, recent presses
    dh: s.duckHeld ? 1 : 0,
    sp: r(s.speed), d: r(s.distance), go: r(s.groundOffset),
    y: r(s.y), vy: r(s.vy), j: s.jumping ? 1 : 0, k: s.ducking ? 1 : 0, sd: s.speedDrop ? 1 : 0, rl: s.released ? 1 : 0,
    o: s.obstacles.map(o => [OBSTACLE_TYPES.indexOf(o.t), r(o.x), r(o.rise), o.size, o.unit,
      o.flight ? FLIGHTS.indexOf(o.flight) : -1, r(o.bobPhase), r(o.age), o.pokemon || 0, o.speedOffset, r(o.rot), r(o.pop)]),
  });
}

export function snapshotState(text) {
  try {
    const d = JSON.parse(text);
    if (!d || d.v !== 1 || !Array.isArray(d.o)) return null;
    const num = (n, lo, hi) => (typeof n === 'number' && Number.isFinite(n) ? Math.min(hi, Math.max(lo, n)) : lo);
    const s = freshState();
    s.noSpawn = true;  // never adds obstacles of its own: they only ever come from the player
    s.runTime = num(d.rt, 0, 1e9);
    const inputs = (Array.isArray(d.in) ? d.in : []).slice(-40)
      .filter(a => Array.isArray(a) && typeof a[0] === 'number' && ['J', 'R', 'D', 'U'].includes(a[1])).map(a => [a[0], a[1]]);
    Object.assign(s, {
      speed: num(d.sp, 0, MAX_SPEED), distance: num(d.d, 0, 1e9), groundOffset: num(d.go, 0, WORLD_W),
      y: num(d.y, 0, 500), vy: num(d.vy, -50, 50), jumping: !!d.j, ducking: !!d.k, speedDrop: !!d.sd, released: !!d.rl,
      duckHeld: !!d.dh,
    });
    s.obstacles = d.o.slice(0, 12).map((a) => {
      const t = Array.isArray(a) && OBSTACLE_TYPES[a[0]];
      if (!t) return null;
      const size = Math.round(num(a[3], 1, MAX_OBSTACLE_LENGTH)), unit = num(a[4], 10, 60);
      return {
        t, size, unit, w: unit * size, x: num(a[1], -200, WORLD_W + 200), rise: num(a[2], 0, 120), gap: 1e9, spawnedNext: true,
        flight: t.type === 'bird' ? FLIGHTS[a[5]] || FLIGHTS[0] : null, bobPhase: num(a[6], 0, 7), age: num(a[7], 0, 1e7),
        pokemon: POKEMON_IDS.includes(a[8]) ? a[8] : null, speedOffset: num(a[9], -1, 1), rot: num(a[10], -1e6, 1e6),
        pop: num(a[11], 0, 1),
      };
    }).filter(Boolean);
    return { s, rt: s.runTime, inputs, score: Math.floor(num(d.sc, 0, 1e7)), over: !!d.over, idle: !!d.idle };
  } catch {
    return null;
  }
}

// The game's pictures, loaded once: Togepi, Pidgey, and the rolling and legendary Pokémon
function useSprites(birdSprite) {
  const sprites = useRef({ pokemon: {} });
  useEffect(() => {
    const load = (src, put) => { const img = new Image(); img.onload = () => put(img); img.src = src; };
    load(PLAYER_SPRITE, (img) => { sprites.current.player = img; });
    load(birdSprite, (img) => { sprites.current.bird = img; });
    POKEMON_IDS.forEach((id) => load(pokemonSprite(birdSprite, id), (img) => { sprites.current.pokemon[id] = img; }));
  }, [birdSprite]);
  return sprites;
}

const WATCH_EVERY_MS = 250;       // how often the watched run is fetched
const WATCH_STALE_MS = 8000;      // a run not heard from in this long has stopped (they've left, or lost connection)
const WATCH_DELAY_MS = 1000;      // how far behind the player the replay runs, so their presses have arrived in time

// What a spectator knows of the run. snaps: its snapshots, oldest first. presses: every press heard of. newest: the
// latest snapshot's clock, and when it arrived. end: the run's end, once known
const fresh = () => ({ snaps: [], presses: new Map(), newest: null, end: null, status: 'waiting', score: 0 });

// Watching someone else play. `load` fetches their latest snapshot: { s, at } or nothing.
// It's a replay a moment behind the player: it starts from their last snapshot and plays their own presses (jump, duck)
// at the exact moments they made them, so what you see is what really happened rather than a guess. Each new snapshot
// puts it back in step, so it can't drift
export function SpectateView({ name, load, onClose, birdSprite = BIRD_SPRITE }) {
  const canvasRef = useRef(null);
  const sprites = useSprites(birdSprite);
  const watch = useRef(fresh());
  const [view, setView] = useState({ status: 'waiting', score: 0 });  // waiting | playing | over

  useEffect(() => {
    let stopped = false, busy = false;
    const poll = async () => {
      setView(v => (v.status === watch.current.status && v.score === watch.current.score ? v
        : { status: watch.current.status, score: watch.current.score }));
      if (busy) return;
      busy = true;
      try {
        const data = await load();
        if (stopped) return;
        const parsed = data && typeof data.s === 'string' && typeof data.at === 'number' && Date.now() - data.at < WATCH_STALE_MS
          ? snapshotState(data.s) : null;
        const w = watch.current;
        if (!parsed || parsed.idle) {  // between runs, or gone: keep showing how their last run ended
          if (!w.end) watch.current = fresh();
          return;
        }
        if (w.newest && (parsed.rt < w.newest.rt - 1000 || (w.end && parsed.rt < w.end.rt))) {
          watch.current = fresh();  // they've started a new run
        }
        const v = watch.current;
        parsed.inputs.forEach(([t, k]) => v.presses.set(`${t}${k}`, [t, k]));
        if (!v.newest || parsed.rt > v.newest.rt) {
          v.snaps.push({ rt: parsed.rt, text: data.s });
          if (v.snaps.length > 24) v.snaps.shift();
          v.newest = { rt: parsed.rt, at: performance.now() };
        }
        if (parsed.over) v.end = { rt: parsed.rt, score: parsed.score };
      } catch { /* try again next time */ } finally { busy = false; }
    };
    poll();
    const timer = setInterval(poll, WATCH_EVERY_MS);
    return () => { stopped = true; clearInterval(timer); };
  }, [load]);

  useEffect(() => {
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return undefined;
    let raf;
    const tick = (now) => {
      const w = watch.current;
      if (w.newest) {
        // Where the replay should be: a moment behind the newest snapshot, and never past the end of the run
        let at = w.newest.rt + (now - w.newest.at) - WATCH_DELAY_MS;
        if (w.end) at = Math.min(at, w.end.rt);
        const base = [...w.snaps].reverse().find(sn => sn.rt <= at) || w.snaps[0];
        at = Math.max(at, base.rt);
        // Worked out afresh from the snapshot every frame (it's quick), so a press that arrives late still counts
        const r = snapshotState(base.text).s;
        // A press is noted with the run's clock as it was when it happened: after that moment's snapshot, before the next
        // step. So a press at the snapshot's own time isn't in it yet, and each press goes in just before its step
        const presses = [...w.presses.values()].filter(([t]) => t >= r.runTime && t <= at).sort((a, b) => a[0] - b[0]);
        let next = 0;
        while (r.runTime < at - 0.5) {
          while (next < presses.length && presses[next][0] <= r.runTime + 1) applyInput(r, presses[next++][1]);
          step(r, Math.min(MS_PER_FRAME, at - r.runTime));
        }
        const finished = w.end && at >= w.end.rt;
        w.status = finished ? 'over' : 'playing';
        w.score = finished ? w.end.score : Math.floor(r.distance * SCORE_COEFFICIENT);
        drawScene(ctx, r, sprites.current, now, !finished);
      } else {
        w.status = w.end ? 'over' : 'waiting';
        drawScene(ctx, freshState(), sprites.current, now, false);
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [sprites]);

  return (
    <div className="w-full flex flex-col items-center gap-3">
      <div className="flex items-center justify-between w-full gap-2" style={{ maxWidth: CANVAS_W }}>
        <h2 className="text-white font-bold text-base truncate">👁 Watching {name}</h2>
        <span className="text-zinc-400 text-xs font-mono flex-shrink-0">SCORE <span className="text-white font-semibold">{String(view.score).padStart(5, '0')}</span></span>
      </div>
      <div className="relative w-full rounded-xl overflow-hidden border border-zinc-800/60 corner-glow select-none"
        style={{ maxWidth: CANVAS_W, aspectRatio: `${CANVAS_W} / ${CANVAS_H}` }}>
        <canvas ref={canvasRef} width={CANVAS_W} height={CANVAS_H} className="w-full h-full block" style={{ imageRendering: 'pixelated' }} />
        {view.status !== 'playing' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 px-4 text-center" style={{ background: 'rgba(0,0,0,0.55)' }}>
            {view.status === 'over' ? (
              <>
                <p className="text-white font-bold text-lg">{name} scored {view.score.toLocaleString()}!</p>
                <p className="text-zinc-400 text-xs">Watching for their next run…</p>
              </>
            ) : <p className="text-zinc-300 text-sm">Waiting for {name} to start a run…</p>}
          </div>
        )}
      </div>
      <button type="button" onClick={onClose}
        className="text-white font-semibold text-sm rounded-lg px-5 py-2.5 transition-all duration-200 active:scale-95"
        style={{ background: 'linear-gradient(135deg, #04aff7, #f42ae4)' }}>
        ← Back to my game
      </button>
    </div>
  );
}

// Where the scores live. On the site it's Firebase's live connection; the Discord Activity passes its own with the
// same three calls, as that connection can't get through Discord's proxy
export const firebaseScores = (database) => ({
  watch: (onScores) => onValue(ref(database, 'pokeDashLeaderboard'), (snap) => onScores(snap.val() || {})),
  get: (uid) => get(ref(database, `pokeDashLeaderboard/${uid}`)).then((snap) => snap.val()),
  save: (uid, entry) => set(ref(database, `pokeDashLeaderboard/${uid}`), entry),
});

// Inside Discord, Discord's own bar (leave, minimise) sits over the top of the page. It says how tall that is through
// --discord-safe-area-inset-top, so the page starts below it, and never less than this
const ACTIVITY_TOP = 'max(calc(var(--discord-safe-area-inset-top, env(safe-area-inset-top, 0px)) + 12px), 64px)';

// `activity`: laid out for Discord (clear of its top bar, the game in the middle). `header`: shown above everything
// `onLive`: given a snapshot of the run a few times a second while playing (and once at the end), for spectators.
// `onGameState`: told when the game goes idle, playing or over
export default function PokeDash({ database, scores: scoresProp, discordUser, onLogin, onGameOver, onLive, onGameState,
  birdSprite = BIRD_SPRITE, activity, header }) {
  const onGameOverRef = useRef(onGameOver);  // told each run's score (the Activity passes it on to the bot)
  onGameOverRef.current = onGameOver;
  const onLiveRef = useRef(onLive);
  onLiveRef.current = onLive;
  const onGameStateRef = useRef(onGameState);
  onGameStateRef.current = onGameState;
  const lastLive = useRef(0);
  const canvasRef = useRef(null);
  const sprites = useSprites(birdSprite);
  const rafRef = useRef(null);
  const stateRef = useRef(freshState());
  const gameStateRef = useRef('idle');
  const [gameState, setGameStateRaw] = useState('idle'); // idle | playing | over
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(0);
  const [flashKey, setFlashKey] = useState(0);
  const [leaderboard, setLeaderboard] = useState([]);
  const myUid = discordUser?.id;
  const scores = useMemo(() => scoresProp || (database ? firebaseScores(database) : null), [scoresProp, database]);

  const setGameState = useCallback((v) => { gameStateRef.current = v; setGameStateRaw(v); onGameStateRef.current?.(v); }, []);

  useEffect(() => {
    if (!scores) return;
    return scores.watch((data) => {
      const list = Object.values(data)
        .sort((a, b) => b.score - a.score)
        .slice(0, 10);
      setLeaderboard(list);
    });
  }, [scores]);

  useEffect(() => {
    try {
      const saved = parseInt(localStorage.getItem(HIGH_SCORE_KEY), 10);
      if (!isNaN(saved)) setHighScore(saved);
    } catch (e) { /* storage blocked */ }
  }, []);

  const startGame = useCallback(() => {
    stateRef.current = freshState();
    setScore(0);
    setGameState('playing');
  }, [setGameState]);

  // ─── Controls ───
  // Mid-run, every press goes through applyInput and is noted with its time, so spectators can replay it exactly
  const input = useCallback((kind) => {
    const s = stateRef.current;
    applyInput(s, kind);
    recordInput(s, kind);
  }, []);

  const pressJump = useCallback(() => {
    const s = stateRef.current;
    const gs = gameStateRef.current;
    if (gs === 'idle') startGame();
    else if (gs === 'over') { if (performance.now() - s.overAt > GAMEOVER_CLEAR_TIME) startGame(); }
    else input('J');
  }, [startGame, input]);

  const releaseJump = useCallback(() => {
    if (gameStateRef.current === 'playing') input('R');
    else applyInput(stateRef.current, 'R');
  }, [input]);

  const pressDuck = useCallback(() => {
    if (gameStateRef.current === 'playing') input('D');
  }, [input]);

  const releaseDuck = useCallback(() => {
    if (gameStateRef.current === 'playing') input('U');
    else applyInput(stateRef.current, 'U');
  }, [input]);

  const draw = useCallback((ctx, now) => drawScene(
    ctx, stateRef.current, sprites.current, now, gameStateRef.current === 'playing'), [sprites]);

  // Touch: the left side ducks and the right side jumps, on the game and on the big buttons under it. Any tap starts
  // (or restarts) a game. Each finger is tracked, so you can hold one side and tap the other
  const held = useRef({});  // pointer id -> 'duck' | 'jump'
  const press = useCallback((id, side) => {
    held.current[id] = side;
    if (gameStateRef.current !== 'playing') pressJump();
    else if (side === 'duck') pressDuck();
    else pressJump();
  }, [pressJump, pressDuck]);
  const release = useCallback((id) => {
    const side = held.current[id];
    if (!side) return;
    delete held.current[id];
    if (Object.values(held.current).includes(side)) return;  // another finger's still on that side
    if (side === 'duck') releaseDuck(); else releaseJump();
  }, [releaseDuck, releaseJump]);
  const sideHandlers = (fixedSide) => ({
    onPointerDown: (e) => {
      e.preventDefault();
      const box = e.currentTarget.getBoundingClientRect();
      press(e.pointerId, fixedSide || (e.clientX - box.left < box.width / 2 ? 'duck' : 'jump'));
    },
    onPointerUp: (e) => release(e.pointerId),
    onPointerCancel: (e) => release(e.pointerId),
    onPointerLeave: (e) => release(e.pointerId),
    onContextMenu: (e) => e.preventDefault(),  // a long press mustn't open a menu
  });

  // ─── Game loop ───
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const tick = (now) => {
      const s = stateRef.current;
      if (gameStateRef.current === 'playing') {
        if (s.lastTime == null) s.lastTime = now;
        const dt = Math.min(now - s.lastTime, 50); // don't jump ahead after a pause or a hidden tab
        s.lastTime = now;
        step(s, dt);

        const newScore = Math.floor(s.distance * SCORE_COEFFICIENT);
        if (newScore !== s.lastScore) {
          if (Math.floor(newScore / 100) > Math.floor(s.lastScore / 100)) setFlashKey(k => k + 1); // flash every 100
          s.lastScore = newScore;
          setScore(newScore);
        }

        if (onLiveRef.current && now - lastLive.current > 250) {
          lastLive.current = now;
          onLiveRef.current(snapshotOf(s, newScore, false), false);
        }

        if (hits(s)) {
          s.overAt = now;
          onLiveRef.current?.(snapshotOf(s, newScore, true), true);
          setGameState('over');
          onGameOverRef.current?.(newScore);
          setHighScore((prevHigh) => {
            if (newScore > prevHigh) {
              try { localStorage.setItem(HIGH_SCORE_KEY, String(newScore)); } catch (e) { /* storage blocked */ }
              return newScore;
            }
            return prevHigh;
          });
          if (scores && myUid && newScore > 0) {
            scores.get(myUid).then((existing) => {
              if (!existing || newScore > existing.score) {
                scores.save(myUid, {
                  uid: myUid,
                  username: discordUser.nickname || discordUser.username,
                  avatar: discordUser.avatar ? `https://cdn.discordapp.com/avatars/${myUid}/${discordUser.avatar}.png` : null,
                  score: newScore,
                });
              }
            });
          }
        }
      } else {
        s.lastTime = null;
      }

      draw(ctx, now);
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [draw, discordUser, scores, myUid, setGameState]);

  useEffect(() => {
    const down = (e) => {
      if (e.code === 'Space' || e.code === 'ArrowUp') {
        e.preventDefault();
        if (!e.repeat) pressJump();
      } else if (e.code === 'ArrowDown') {
        e.preventDefault();
        if (!e.repeat) pressDuck();
      }
    };
    const up = (e) => {
      if (e.code === 'Space' || e.code === 'ArrowUp') releaseJump();
      else if (e.code === 'ArrowDown') releaseDuck();
    };
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    return () => { window.removeEventListener('keydown', down); window.removeEventListener('keyup', up); };
  }, [pressJump, releaseJump, pressDuck, releaseDuck]);

  // ─── RENDER: Not logged in ─────────────────────────────────────────────────
  if (!discordUser) {
    return (
      <div className="relative flex-1 flex flex-col items-center justify-center gap-4 px-6 text-center">
        <div
          className="fixed left-0 right-0 bottom-0 pointer-events-none"
          style={{ top: '100px', background: 'linear-gradient(160deg, rgba(4,175,247,0.08), rgba(0,0,0,0.4))' }}
        />
        <img src="/pball.svg" alt="" className="relative z-10 h-12 w-12 animate-spin" style={{ animationDuration: '2s', filter: 'brightness(0) invert(1)' }} />
        <div className="relative z-10">
          <h2 className="text-white font-bold text-xl">Cheeky Dash</h2>
          <p className="text-white text-sm mt-1">Get Togepi past the obstacles — how far can you get?</p>
        </div>
        <div className="relative z-10 flex flex-col gap-1 rounded-xl px-4 py-3 border border-zinc-800/40 backdrop-blur-sm" style={{ background: 'rgba(0,0,0,0.45)' }}>
          <p className="text-white text-sm">You need to log in with Discord to play!</p>
          <button
            onClick={onLogin}
            className="flex items-center justify-center gap-1.5 text-white font-semibold text-xs rounded-lg px-4 py-2 transition-all duration-200 active:scale-95"
            style={{ background: 'linear-gradient(135deg, #04aff7, #f42ae4)' }}
          >
            <LogIn className="h-3.5 w-3.5 flex-shrink-0" />
            Log in with Discord
          </button>
        </div>
      </div>
    );
  }

  // ─── RENDER: Logged in — the game ──────────────────────────────────────────
  const pad = (n) => String(n).padStart(5, '0');
  return (
    <div className="flex-1 flex flex-col items-center px-4 py-6 gap-3" style={activity ? { paddingTop: ACTIVITY_TOP } : undefined}>
      <style>{`
        @keyframes scoreFlash { 0%, 100% { opacity: 1; } 16%, 50%, 83% { opacity: 0; } 33%, 66% { opacity: 1; } }
        .score-flash { animation: scoreFlash 0.75s steps(1) both; }
      `}</style>
      {header}
      {leaderboard.length > 0 && (
        <div className="w-full rounded-xl border border-zinc-800/60 corner-glow bg-gradient-to-br from-zinc-800/85 to-black/90 px-3 py-2.5" style={{ maxWidth: CANVAS_W }}>
          <style>{`
            @keyframes trophyBounce { 0%, 100% { transform: translateY(0) rotate(0deg); } 25% { transform: translateY(-2px) rotate(-8deg); } 50% { transform: translateY(0) rotate(0deg); } 75% { transform: translateY(-2px) rotate(8deg); } }
            .trophy-bounce { display: inline-block; animation: trophyBounce 1.4s ease-in-out infinite; }
          `}</style>
          <div className="flex items-center gap-1.5">
            <span className="trophy-bounce" style={{ fontSize: '12px' }}>🏆</span>
            <p className="text-white text-[10px] font-bold uppercase tracking-wider">Leaderboard</p>
            <p className="text-zinc-400 text-[10px] font-bold uppercase tracking-wider">Top {leaderboard.length}</p>
          </div>
          <div className="flex flex-col gap-1 mt-1.5">
            {leaderboard.map((entry, i) => (
              <div
                key={entry.uid}
                className="flex items-center gap-2"
                style={entry.uid === myUid ? { background: 'rgba(4,175,247,0.12)', borderRadius: '6px', padding: '2px 4px', margin: '-2px -4px' } : undefined}
              >
                <span className="text-zinc-400 font-mono text-[10px] w-4 flex-shrink-0 text-center">{i + 1}</span>
                <span className="text-white text-[10px] font-medium flex-1 truncate">{entry.username}</span>
                <span className="text-white text-[10px] font-semibold font-mono flex-shrink-0">{entry.score}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex-1 flex flex-col items-center gap-3 w-full">
      {/* In Discord, space above the game as well as below, so it sits in the middle */}
      {activity && <div className="flex-1 min-h-0" aria-hidden="true" />}
      <div className="flex items-center justify-between w-full" style={{ maxWidth: CANVAS_W }}>
        <h2 className="text-white font-bold text-base">Cheeky Dash</h2>
        <div className="flex items-center gap-3 text-xs font-mono">
          {highScore > 0 && <span className="text-zinc-500">HI <span className="text-zinc-300 font-semibold">{pad(highScore)}</span></span>}
          <span key={flashKey} className={flashKey > 0 ? 'score-flash text-zinc-400' : 'text-zinc-400'}>
            SCORE <span className="text-white font-semibold">{pad(score)}</span>
          </span>
        </div>
      </div>

      <div
        className="relative w-full rounded-xl overflow-hidden border border-zinc-800/60 corner-glow select-none"
        style={{ maxWidth: CANVAS_W, aspectRatio: `${CANVAS_W} / ${CANVAS_H}`, cursor: 'pointer', touchAction: 'none' }}
        {...sideHandlers()}
      >
        <canvas
          ref={canvasRef}
          width={CANVAS_W}
          height={CANVAS_H}
          className="w-full h-full block"
          style={{ imageRendering: 'pixelated' }}
        />

        {gameState !== 'playing' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 pointer-events-none" style={{ background: 'rgba(0,0,0,0.45)' }}>
            {gameState === 'idle' && (
              <>
                <p className="text-white font-bold text-lg">Tap anywhere to start</p>
                <p className="text-zinc-300 text-xs">Left side ducks · right side jumps (or ↓ and Space)</p>
              </>
            )}
            {gameState === 'over' && (
              <>
                <p className="text-white font-bold text-lg">Game Over</p>
                <p className="text-zinc-300 text-xs">Score: {score}{score >= highScore && score > 0 ? ' — New High Score!' : ''}</p>
                <p className="text-zinc-400 text-[11px] mt-1">Tap anywhere to try again</p>
              </>
            )}
          </div>
        )}
      </div>

      {/* A gap, then the two square touch areas at the bottom, one under each thumb: left ducks, right jumps */}
      <div className="flex-1 min-h-[16px]" aria-hidden="true" />
      <div className="w-full flex justify-between gap-3 pb-2 select-none"
        style={{ maxWidth: CANVAS_W, touchAction: 'none', WebkitTapHighlightColor: 'transparent' }}>
        {[['duck', '⬇', 'Duck', 'Under birds · mid-jump drops fast', '4,175,247'],
          ['jump', '⬆', 'Jump', 'Hold for a higher jump', '244,42,228']].map(([side, arrow, label, hint, rgb]) => (
          <div key={side} {...sideHandlers(side)} role="button" aria-label={label}
            className="flex flex-col items-center justify-center gap-1 rounded-2xl cursor-pointer aspect-square"
            style={{ width: 'min(46%, 220px)', touchAction: 'none', border: `2px dashed rgba(${rgb},0.45)`, background: `rgba(${rgb},0.08)` }}>
            <span className="text-4xl leading-none pointer-events-none" style={{ color: `rgb(${rgb})` }} aria-hidden="true">{arrow}</span>
            <span className="text-white font-black text-lg uppercase tracking-wider pointer-events-none">{label}</span>
            <span className="text-zinc-400 text-[10px] text-center px-2 pointer-events-none">{hint}</span>
          </div>
        ))}
      </div>
      </div>
    </div>
  );
}
